import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, type GridColDef, type GridValueGetterParams } from '@mui/x-data-grid';
import { useCancelOrderMutation, useGetMyOrdersQuery } from 'store/api/orderApi';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'utils/constants';
import WriteReviewPopup from 'components/review/write-review-popup/WriteReviewPopup';
import { formatDualCurrency } from 'utils/currency';
import useWindowWidth from 'hooks/useWindowWidth';
import Pagination from 'components/pagination/Pagination';
import { Button as MuiButton, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

const ProfileOrders = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowWidth();
  const [queryParams, setQueryParams] = useState({ page: DEFAULT_PAGE });
  const { data: orders, isLoading, refetch } = useGetMyOrdersQuery(queryParams);
  const [cancelOrder, { isLoading: isCancelling }] = useCancelOrderMutation();
  const [cancelOrderId, setCancelOrderId] = useState<number | null>(null);
  const [cancelError, setCancelError] = useState<boolean>(false);
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false);
  const [currentSeller, setCurrentSeller] = useState<{ id: number, name: string }>({
    id: 0,
    name: ''
  });

  const handleReviewClick = (sellerId: number, sellerUsername: string) => {
    setCurrentSeller({
      id: sellerId,
      name: sellerUsername
    });
    setReviewPopupOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelOrderId(null);
    setCancelError(false);
  };

  const confirmCancel = async () => {
    if (cancelOrderId === null) return;

    const result = await cancelOrder({ orderId: cancelOrderId, reason: t('profile.payments.cancelOrderReason') });

    if ('error' in result) {
      // Any refusal (courier already has it, or Speedy declined) gets the
      // same message - never the server's English text.
      setCancelError(true);
      return;
    }

    closeCancelDialog();
    void refetch();
  };

  // Shown while the shipment is not yet handed over (operation code 0);
  // the server re-checks this, so this only hides a button that would fail.
  const renderStatus = (row: any) => {
    if (row.isCancelled) {
      return <span className="profile-orders-table-cancelled">{t('profile.payments.cancelled')}</span>;
    }

    return (
      <div className="flex flex-column">
        {t(`operationCodes.${row.operationCode.toString()}`)}
        {row.operationCode === 0 && (
          <span
            className="profile-orders-table-cancel-order"
            role="button"
            onClick={() => {
              setCancelOrderId(row.id);
            }}
          >
            {t('profile.payments.cancelOrder')}
          </span>
        )}
      </div>
    );
  };

  const dataColumnsDesktop: GridColDef[] = [
    {
      field: 'createdOn',
      headerName: t('profile.payments.date'),
      flex: 1,
      disableColumnMenu: true,
      valueGetter: (params: GridValueGetterParams) => {
        return new Date(params.row.createdOn).toLocaleDateString();
      }
    },
    {
      field: 'id',
      flex: 1,
      headerName: t('profile.payments.numberOfOrder'),
      disableColumnMenu: true
    },
    {
      field: 'sellerUsername',
      headerName: t('profile.payments.user'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className="flex flex-column">
          {params.row.sellerUsername}
          <span
            className="profile-orders-table-write-review"
            onClick={() => {
              handleReviewClick(params.row.sellerId, params.row.sellerUsername);
            }}
          >
            {t('profile.payments.writeReview')}
          </span>
        </div>
      )
    },
    {
      field: 'totalPrice',
      headerName: t('profile.payments.paidAmount'),
      disableColumnMenu: true,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        return formatDualCurrency(params.row.totalPrice.amount, params.row.totalPrice.currency);
      }
    },
    {
      field: 'status',
      headerName: t('profile.payments.status'),
      disableColumnMenu: true,
      width: 250,
      renderCell: (params) => renderStatus(params.row)
    }
  ];

  const dataColumnsMobile: GridColDef[] = [
    {
      field: 'createdOn',
      headerName: t('profile.payments.date'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className="flex flex-column justify-center align-center">
          <span className="profile-orders-table-order-id">№ {params.row.id}</span>
          {new Date(params.row.createdOn).toLocaleDateString()}
        </div>
      )
    },
    {
      field: 'sellerUsername',
      headerName: t('profile.payments.user'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className="flex flex-column justify-center align-center">
          {params.row.sellerUsername}
          <span
            className="profile-orders-table-write-review"
            onClick={() => {
              handleReviewClick(params.row.sellerId, params.row.sellerUsername);
            }}
          >
            {t('profile.payments.writeReview')}
          </span>
        </div>
      )
    },
    {
      field: 'totalPrice',
      headerName: t('profile.payments.paidAmount'),
      flex: 1,
      disableColumnMenu: true,
      valueGetter: (params: GridValueGetterParams) => {
        return formatDualCurrency(params.row.totalPrice.amount, params.row.totalPrice.currency);
      }
    },
    {
      field: 'status',
      headerName: t('profile.payments.status'),
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => renderStatus(params.row)
    }
  ];

  if (isLoading) {
    return <div>{t('profile.payments.loadingOrders')}</div>;
  }

  if (orders?.models.length === 0) {
    return <div>{t('profile.payments.noOrders')}</div>;
  }

  return (
    <div>
      <DataGrid
        className="profile-data-grid profile-orders-table"
        rows={orders?.models ?? []}
        columns={isMobile ? dataColumnsMobile : dataColumnsDesktop}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE }
          }
        }}
        getRowHeight={() => 'auto'}
        pageSizeOptions={[DEFAULT_PAGE_SIZE]}
        disableColumnFilter
        rowCount={orders?.totalCount ?? 0}
        paginationMode='server'
        onPaginationModelChange={(e) => {
          setQueryParams({ page: e.page + 1 });
        }}
      />
      <Pagination
        count={orders?.totalPages ?? 1}
        page={queryParams.page}
        onChange={(value) => {
          setQueryParams({ page: value });
        }}
      />
      {cancelOrderId !== null && (
        <Dialog maxWidth="xs" fullWidth open onClose={closeCancelDialog}>
          <DialogTitle>{t('profile.payments.cancelOrder')} № {cancelOrderId}</DialogTitle>
          <DialogContent dividers>
            {t('profile.payments.cancelOrderConfirm')}
            {cancelError && (
              <div className="profile-orders-cancel-error" role="alert">
                {t('profile.payments.cancelOrderFailed')}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={closeCancelDialog}>{t('common.no')}</MuiButton>
            <MuiButton
              disabled={isCancelling}
              className="profile-orders-cancel-confirm"
              onClick={() => {
                void confirmCancel();
              }}
            >
              {t('common.yes')}
            </MuiButton>
          </DialogActions>
        </Dialog>
      )}
      {reviewPopupOpen && (
        <WriteReviewPopup
          sellerId={currentSeller.id}
          sellerUsername={currentSeller.name}
          open={reviewPopupOpen}
          onClose={() => {
            setReviewPopupOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ProfileOrders;
